import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { getPaymentStatus } from "@/lib/paymentsApi";

const MAX_POLLS = 8;

export const PaymentSuccessPage = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let polls = 0;
    let timer;
    const poll = async () => {
      if (!sessionId) {
        setState("error");
        return;
      }
      try {
        const data = await getPaymentStatus(sessionId);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (polls >= MAX_POLLS) {
          setState("pending");
          return;
        }
        polls += 1;
        timer = setTimeout(poll, 2000);
      } catch (e) {
        setState("error");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background text-foreground font-sans flex flex-col">
      <Navbar />
      <main className="flex-1 grid place-items-center px-6 py-24">
        <div className="max-w-md w-full rounded-3xl border border-white/10 bg-card p-10 text-center" data-testid="payment-success-page">
          {state === "checking" && (
            <>
              <Loader2 className="h-12 w-12 mx-auto text-venka-green-light animate-spin" />
              <h1 className="mt-6 font-serif text-2xl text-white">{t("paymentPage.checking")}</h1>
              <p className="mt-2 text-sm text-slate-400">{t("paymentPage.checkingSub")}</p>
            </>
          )}
          {state === "paid" && (
            <>
              <CheckCircle2 className="h-14 w-14 mx-auto text-venka-green-light" data-testid="payment-paid-icon" />
              <h1 className="mt-6 font-serif text-3xl text-white">{t("paymentPage.paidTitle")}</h1>
              <p className="mt-3 text-sm text-slate-400">
                {t("paymentPage.paidText", { tier: info?.tier || "" })}
              </p>
              <div className="mt-8 flex flex-col gap-2">
                <Button asChild className="rounded-full brand-gradient text-white"><Link to="/apply">{t("paymentPage.startApp")} <ArrowRight className="ml-1.5 h-4 w-4" /></Link></Button>
                <Button asChild variant="outline" className="rounded-full border-white/20 text-slate-100 hover:bg-white/5"><Link to="/">{t("paymentPage.backHome")}</Link></Button>
              </div>
            </>
          )}
          {state === "pending" && (
            <>
              <Loader2 className="h-12 w-12 mx-auto text-amber-400" />
              <h1 className="mt-6 font-serif text-2xl text-white">{t("paymentPage.pendingTitle")}</h1>
              <p className="mt-3 text-sm text-slate-400">{t("paymentPage.pendingText")}</p>
              <Button asChild className="mt-8 rounded-full brand-gradient text-white"><Link to="/packages">{t("paymentPage.backPackages")}</Link></Button>
            </>
          )}
          {state === "error" && (
            <>
              <XCircle className="h-14 w-14 mx-auto text-red-400" />
              <h1 className="mt-6 font-serif text-2xl text-white">{t("paymentPage.errorTitle")}</h1>
              <p className="mt-3 text-sm text-slate-400">{t("paymentPage.errorText")}</p>
              <Button asChild className="mt-8 rounded-full brand-gradient text-white"><Link to="/packages">{t("paymentPage.backPackages")}</Link></Button>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;
